import {AfterViewInit, Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {ITableHeader, TableComponent} from '../../shared/table/TableComponent';
import {
    Agency,
    AssistanceApplicationStatus,
    AssistanceApplicationType,
    FundTypeClient,
    PagedListOfAssistanceApplicationListPreview,
    User,
    UserType
} from '../../../services/api.client';
import {AgencyListModalComponent} from '../agency/AgencyListModalComponent';
import {extractEnumNames, splitCamelCase} from '../../../utilities/Util';
import {UntypedFormControl} from '@angular/forms';
import {Observable, of} from 'rxjs';
import {SecurityService} from '../../../services/SecurityService';
import {AssistanceApplicationService} from '../../public/application/assistance-application.service';
import {AgencyService} from '../../../services/AgencyService';
import {NotificationService} from '../../../services/NotificationService';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {InternalDraftService} from '../../../services/internal-draft.service';

const ASSISTANCE_APPLICATION_TYPE = AssistanceApplicationType.UFF;

@Component({
    templateUrl: './UffAssistanceApplicationsListComponent.html',
    styleUrls: ['./UffAssistanceApplicationsListComponent.scss'],
    standalone: false
})

export class UffAssistanceApplicationsListComponent implements OnInit, AfterViewInit {

    draftCount = 0;
    showDraftsTab = false;

    constructor(
        private assistanceApplicationService: AssistanceApplicationService,
        private _agencyService: AgencyService,
        private _fundTypeClient: FundTypeClient,
        private _securityService: SecurityService,
        private _notificationService: NotificationService,
        private router: Router,
        private internalDraftService: InternalDraftService) {
        this.currentUser$ = this._securityService.currentUser$;
    }
    private allStatuses = extractEnumNames(AssistanceApplicationStatus);
    statuses: { key: string, value: string }[] = [];
    selectedAgencyId: string;
    searchValue: string;
    appStatus = new UntypedFormControl('');

    @ViewChild(TableComponent)
    table: TableComponent;

    currentUser: User;
    currentUser$: Observable<User>;

    // 003-retire-ngrx (US2): page state is component-local now — the list is a
    // same-page fetch->render round-trip.
    assistanceApplicationList: PagedListOfAssistanceApplicationListPreview;

    regionAdminAgencies: Agency[];

    // sorting
    currentSortName = 'EnteredDate';
    currentSorting = true;

    tableHeaders: ITableHeader[] = [
        {text: 'Fund', sortKey: 'Fund|FundName'},
        {text: 'Last Name', sortKey: 'Client|LastName'},
        {text: 'First Name', sortKey: 'Client|FirstName'},
        {text: 'SSN', sortKey: 'Client|Last4Ssn'},
        {text: 'Created', sortKey: 'EnteredDate', class: 'hidden-sm-down', ascending: false},
        {text: 'Status', sortKey: 'Status'},
        {text: 'Agency', sortKey: 'CreatedByAgency|AgencyName'},
        {text: 'Total Need', sortKey: 'TotalNeed'},
    ];

    // pagination
    limit = 10;
    currentPage = 1;
    maxSize = 1;
    totalCount: number;

    // MODAL
    @ViewChild('agencyListModal')
    agencyListModal: AgencyListModalComponent;
    @Output('onShowAgencyModal') _showAgencies = new EventEmitter();

    // selected agency
    agency: Agency;
    selectedAgencyName = '-- Select Agency --';

    ngOnInit(): void {
        this.internalDraftService.getCount(ASSISTANCE_APPLICATION_TYPE).subscribe(count => this.draftCount = count);

        this._fundTypeClient.getStatusesByAssistanceApplicationType(ASSISTANCE_APPLICATION_TYPE).subscribe(allowedStatuses => {
            this.statuses = extractEnumNames(AssistanceApplicationStatus).filter(s => (allowedStatuses as unknown as string[]).includes(s.key));
        });

        this.currentUser$.pipe(
            tap(user => {
                this.currentUser = user;
                const hasAccessToApplication = this.assistanceApplicationService.hasAccess(AssistanceApplicationType.UFF, user);
                if (!hasAccessToApplication) {
                    this._notificationService.showError('You do not have access to view assistance applications.');
                    this.router.navigate(['/not-authorized']);
                    return;
                }
                if (!this.userHasAccessToAllAgencyApplications(user) && user.agencyId) {
                    this.selectedAgencyId = user.agencyId;
                    this.selectedAgencyName = user.agency.agencyName;
                }
            }),
            switchMap(() => this._agencyService.getRegionAdminAgencies()),
            catchError(error => {
                this._notificationService.showError('There was a problem loading the region admin agencies.');
                return of([]); // Return an empty array or appropriate default value on error.
            })
        ).subscribe(agencies => {
            this.regionAdminAgencies = agencies;
        });
    }

    get isAdmin(): boolean {
        return this.currentUser.userType === UserType.SystemAdmin;
    }

    setSearchValue(value: string): void {
        this.searchValue = value;
    }

    sortCases(options: ITableHeader): void {
        this.currentSortName = options.sortKey;
        this.currentSorting = !options.ascending;
        this.searchAssistanceApplications();
    }

    setLimit(limit: number): void {// # of rows per page
        this.limit = limit;
        this.searchAssistanceApplications();
    }

    setPage(page: number): void {
        setTimeout(() => {
            this.currentPage = page; // set current page
            this.searchAssistanceApplications();
        });
    }

    searchAssistanceApplications(): void {
        const request = this.assistanceApplicationService.getAssistanceApplicationsPaged(
            ASSISTANCE_APPLICATION_TYPE,
            this.searchValue,
            null,
            null,
            this.appStatus.value,
            null,
            [],
            this.currentSortName,
            this.limit,
            (this.currentPage - 1) * this.limit,
            this.currentSorting
        );
        this.table.request = request;
        request.subscribe({
            next: (pagedList) => {
                this.assistanceApplicationList = pagedList;
                this.totalCount = pagedList?.totalCount;
            },
            error: () => { /* 003-retire-ngrx (US2): the service already shows the error toast */ }
        });
    }

    clearFilters(): void {
        this.searchValue = '';
        this.selectedAgencyId = '';
        this.selectedAgencyName = '-- Select Agency --';
        this.appStatus.setValue('');

        if (!this.userHasAccessToAllAgencyApplications(this.currentUser)) {
            this.selectedAgencyId = this.currentUser.agencyId;
            this.selectedAgencyName = this.currentUser.agency.agencyName;
        }

        this.searchAssistanceApplications();
    }

    showAgencyModal(): boolean {
        this.agencyListModal.open();
        return false;
    }

    onAgencySelected(selectedAgency): void {
        if (selectedAgency) {
            selectedAgency.agencyName ? this.selectedAgencyName = selectedAgency.agencyName : this.selectedAgencyName = '';
            this.selectedAgencyId = selectedAgency.id;
        } else {
            this.selectedAgencyName = '';
            this.selectedAgencyId = '';
        }
    }

    isDisabledAgency(): boolean {
        return !this.userHasAccessToAllAgencyApplications(this.currentUser);
    }


    private userHasAccessToAllAgencyApplications(user: User): boolean {
        const userIsSystemAdmin = user.userType === UserType.SystemAdmin;

        return userIsSystemAdmin;
    }

    formatStatus(status): string {
        let statusText: { key: string, value: string } = {key: '', value: ''};
        if (typeof status === 'number') {
            statusText = this.allStatuses.find(type => type.value === String(status));
        } else if (typeof status === 'string') {
            statusText = this.allStatuses.find(type => type.key.toLowerCase() === status.toLowerCase());
        }
        return splitCamelCase(statusText?.key) || '';
    }

    ngAfterViewInit(): void {
        this.searchAssistanceApplications();
    }

    protected readonly ASSISTANCE_APPLICATION_TYPE = ASSISTANCE_APPLICATION_TYPE;
}
